import { TableCell, TableRow } from "@/components/ui/table";
import { ChevronRight, BookOpen, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { inr, inrShort } from "@/pages/Dashboard";

const BUCKET_BADGE = {
  "Not Due": "bg-emerald-100 text-emerald-700", "0-30": "bg-sky-100 text-sky-700",
  "31-60": "bg-amber-100 text-amber-700", "61-90": "bg-orange-100 text-orange-700",
  "91-180": "bg-red-100 text-red-700", "180+": "bg-red-200 text-red-900",
};

// Plain-JS recursion (no self-referencing JSX component) to produce a flat list of visible rows.
export function flattenTree(nodes, expanded, depth = 0, prefix = "") {
  const rows = [];
  (nodes || []).forEach((node, i) => {
    const id = prefix ? `${prefix}.${i}` : `${i}`;
    rows.push({ kind: "node", id, depth, node });
    if (expanded.has(id)) {
      if (node.children && node.children.length) {
        rows.push(...flattenTree(node.children, expanded, depth + 1, id));
      } else if (node.level === "invoice" && node.detail) {
        rows.push({ kind: "detail", id: `${id}~d`, depth: depth + 1, detail: node.detail });
      }
    }
  });
  return rows;
}

function Field({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-400 w-28 shrink-0">{label}</span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  );
}

export function NodeRow({ row, buckets, expanded, onToggle, testPrefix, onLedger, ko }) {
  const { node, depth, id } = row;
  const isLeaf = node.level === "invoice";
  const k = isLeaf && ko ? ko[`${node.detail?.vendor_code || ""}|${node.key}`] : null;
  const isParty = node.level === "supplier" || node.level === "customer";
  const canExpand = (node.children && node.children.length > 0) || isLeaf;
  const isOpen = expanded.has(id);
  return (
    <TableRow
      className="border-b border-slate-200 hover:bg-slate-50/60 cursor-pointer"
      onClick={() => canExpand && onToggle(id)}
      data-testid={`${testPrefix}-node-${node.key}`}
    >
      <TableCell className="text-sm">
        <div className="flex items-center gap-2" style={{ paddingLeft: depth * 18 }}>
          {canExpand
            ? <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
            : <span className="w-3.5" />}
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1 py-0.5 shrink-0">{node.level_label}</span>
          <span className={isLeaf ? "font-mono text-slate-700" : "font-medium text-slate-800"}>{node.key}</span>
          {isLeaf && ko && (k ? (
            <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${k.is_bill ? (k.balance <= 0.5 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800") : "bg-sky-100 text-sky-800"}`}
              data-testid={`${testPrefix}-ko-badge-${node.key}`} title={`${k.status} · ${k.links.map((x) => `${x.doc}: ${inr(x.amount)} (${x.basis})`).join("; ")}`}>
              {k.is_bill ? `Paid ${inrShort(k.applied)} · Bal ${inrShort(k.balance)}` : `Adjusted ${inrShort(k.applied)} · Left ${inrShort(Math.abs(k.balance))}`}
            </span>
          ) : node.total > 0 ? (
            <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-500" data-testid={`${testPrefix}-ko-badge-${node.key}`}>Unpaid</span>
          ) : null)}
          {node.level === "wbs" && node.project_name && <span className="text-xs text-slate-400 truncate">· {node.project_code} {node.project_name}</span>}
          {node.financing_role && (
            <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${node.financing_role === "financier" ? "bg-violet-100 text-violet-700" : "bg-indigo-100 text-indigo-700"}`} data-testid={`${testPrefix}-financing-badge-${node.key}`} title="Supplier-financing posting (TReDS / financier) — expand for details">
              {node.financing_role === "financier" ? "Financier payable" : "Financed advance"}
            </span>
          )}
          {node.level === "supplier" && (
            <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${node.msme === "Yes" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`msme-badge-${node.key}`}>
              {node.msme === "Yes" && node.msme_no ? `MSME · ${node.msme_no}` : `MSME: ${node.msme}`}
            </span>
          )}
          {node.level === "customer" && (
            <span className="text-[10px] font-mono font-semibold rounded-full px-1.5 py-0.5 bg-sky-50 text-sky-800" data-testid={`${testPrefix}-tax-number-${node.key}`} title="Tax Number 3 (GSTIN) from the SAP business partner">
              {node.gstin ? `Tax No. 3: ${node.gstin}` : "Tax No. 3: —"}
            </span>
          )}
          {node.level === "supplier" && node.msme_interest > 0 && (
            <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5 bg-red-100 text-red-700" data-testid={`msme-interest-${node.key}`} title="Estimated MSMED Act interest on dues beyond 45 days">
              Interest ~{inrShort(node.msme_interest)}
            </span>
          )}
          {isParty && node.party_code && (
            <Link to={node.level === "supplier" ? `/vendor/${encodeURIComponent(node.party_code)}` : `/sales/customer/${encodeURIComponent(node.party_code)}`} onClick={(e) => e.stopPropagation()}
              title={node.level === "supplier" ? "Vendor 360° profile" : "Customer 360° profile"}
              className={`${onLedger ? "ml-auto" : "ml-auto"} inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800 hover:bg-sky-700 hover:text-white transition-colors shrink-0`}
              data-testid={`${testPrefix}-profile-${node.party_code}`}>
              <UserRound className="h-3 w-3" /> Profile
            </Link>
          )}
          {isParty && onLedger && node.party_code && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onLedger(node); }}
              title="Open this party's Ledger with the same bracket highlighted"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors shrink-0"
              data-testid={`${testPrefix}-ledger-jump-${node.party_code}`}>
              <BookOpen className="h-3 w-3" /> Ledger
            </button>
          )}
        </div>
      </TableCell>
      {buckets.map((b) => (
        <TableCell key={b} className="text-right text-sm text-slate-600">{node.buckets[b] ? inrShort(node.buckets[b]) : "—"}</TableCell>
      ))}
      <TableCell className="text-right text-sm font-bold text-slate-900">{inrShort(node.total)}</TableCell>
      <TableCell className="text-right text-sm text-slate-500">{node.count}</TableCell>
    </TableRow>
  );
}

function PaymentsTable({ payments }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-400 uppercase tracking-wider text-left">
          <th className="py-1 font-medium">Doc No</th>
          <th className="py-1 font-medium">Date</th>
          <th className="py-1 font-medium">Method</th>
          <th className="py-1 font-medium">Bank / UTR</th>
          <th className="py-1 font-medium text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((p, i) => (
          <tr key={i} className="border-t border-slate-100 text-slate-600">
            <td className="py-1 font-mono">{p.payment_doc}</td>
            <td className="py-1">{p.payment_date}</td>
            <td className="py-1">{p.method}</td>
            <td className="py-1">{p.bank} · <span className="font-mono text-[10px]">{p.utr}</span></td>
            <td className="py-1 text-right font-medium text-slate-800">{inr(p.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DetailRow({ detail, colSpan, testPrefix, ko }) {
  const d = detail;
  const k = ko ? ko[`${d.vendor_code || ""}|${d.invoice_no}`] : null;
  const isAP = Boolean(d.vendor_name);
  const partyValue = `${isAP ? d.vendor_name : d.customer_name} (${isAP ? d.vendor_code : d.customer_code})`;
  const bucketBadge = <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${BUCKET_BADGE[d.bucket]}`}>{d.bucket}</span>;
  const msmeValue = d.vendor_msme === "Yes" ? `Yes · ${d.msme_no}` : "No";
  const noneMsg = isAP ? "No payments recorded against this invoice yet." : "No receipts recorded against this invoice yet.";
  return (
    <TableRow className="bg-slate-50/70">
      <TableCell colSpan={colSpan} className="p-0">
        <div className="px-10 py-4 fade-in-up grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={`${testPrefix}-invoice-detail-${d.invoice_no}`}>
          <div className="space-y-1.5 text-xs">
            <Field label="Party" value={partyValue} />
            <Field label="Inv.No. (SAP Ref)" value={`${d.invoice_no} · ${d.doc_type} · ${d.category}`} />
            {d.doc_currency && d.doc_currency !== "INR" && d.amount_fc != null && <Field label="Document currency" value={<span className="text-amber-800 font-semibold" data-testid={`${testPrefix}-fc-${d.invoice_no}`}>{d.doc_currency} {Number(d.amount_fc).toLocaleString("en-IN", { maximumFractionDigits: 2 })} open (INR equivalent shown in the ageing)</span>} />}
            <Field label={isAP ? "Supplier Ref" : "Invoice No"} value={d.ref_no || "—"} />
            <Field label="WBS / PO" value={<span data-testid={`${testPrefix}-wbs-po-${d.invoice_no}`}>{d.wbs_element} · {d.po_number}
              {d.project_source === "text" && <span className="ml-1.5 rounded-full bg-amber-100 text-amber-900 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`${testPrefix}-inferred-${d.invoice_no}`}>inferred from line text</span>}
              {d.wbs_element === "UNASSIGNED" && d.project_gap_reason && <span className="ml-1.5 rounded-full bg-slate-200 text-slate-700 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`${testPrefix}-gap-reason-${d.invoice_no}`}>{d.project_gap_reason}</span>}
            </span>} />
            {d.billed_amount ? <Field label="Billed / TDS / Net" value={<span data-testid={`${testPrefix}-billed-tds-${d.invoice_no}`}>{inr(d.billed_amount)} billed incl. GST · TDS {inr(d.tds_amount)} · <b>{inr(d.billed_amount - d.tds_amount)} net payable</b></span>} /> : null}
            {d.cost_center && <Field label="Cost Center" value={<span data-testid={`${testPrefix}-cost-center-${d.invoice_no}`}>{d.cost_center}</span>} />}
            <Field label="Invoice / Due" value={`${d.invoice_date} → ${d.due_date}`} />
            <Field label="Ageing" value={bucketBadge} />
            <Field label="Payment Terms" value={d.payment_terms || "—"} />
            {isAP && <Field label="MSME" value={msmeValue} />}
            {isAP && d.msme_days_overdue > 0 && (
              <Field label="MSME Overdue" value={<span className="text-red-600 font-semibold">{d.msme_days_overdue} days past 45 · Interest ~{inr(d.msme_interest)}</span>} />
            )}
            {d.gstin && <Field label={isAP ? "GSTIN" : "Tax Number 3 (GSTIN)"} value={d.gstin} />}
            {d.financing && (
              <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/60 p-2.5" data-testid={`${testPrefix}-financing-${d.invoice_no}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1">Supplier financing (SAP doc type {d.doc_type})</p>
                <p className="text-xs text-slate-700">
                  {d.financing.role === "financier"
                    ? <>This payable to the financier settles <strong>{d.financing.counterparty}</strong> ({inr(d.financing.counterparty_amount)}) on your behalf; that supplier's advance / cleared invoice sits on its own account in this same SAP document.</>
                    : <>Paid by <strong>{d.financing.counterparty}</strong> on your behalf — the financier's payable of {inr(d.financing.counterparty_amount)} is open under its own name in this same SAP document.</>}
                  {d.financing.gap ? <> Difference of <strong>{inr(Math.abs(d.financing.gap))}</strong> = TDS / charges posted to a GL account, not to any vendor.</> : null}
                </p>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {isAP && (
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`${testPrefix}-bank-${d.invoice_no}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Beneficiary Bank Details</p>
                <p className="text-sm text-slate-800">{d.bank_name || "—"}</p>
                <p className="text-xs text-slate-500 font-mono">A/c {d.bank_account || "—"} · {d.bank_ifsc || "—"}</p>
              </div>
            )}
            {isAP && ko && (
              <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`${testPrefix}-ko-${d.invoice_no}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Knock-off (payments matched to this {k && !k.is_bill ? "payment" : "bill"})</p>
                {k ? (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div><p className="text-slate-400">{k.is_bill ? "Bill open" : "Payment"}</p><p className="font-semibold tabular-nums">{inr(Math.abs(k.open))}</p></div>
                      <div><p className="text-slate-400">{k.is_bill ? "Paid / adjusted" : "Adjusted to bills"}</p><p className="font-semibold tabular-nums text-emerald-700" data-testid={`${testPrefix}-ko-paid-${d.invoice_no}`}>{inr(k.applied)}</p></div>
                      <div><p className="text-slate-400">{k.is_bill ? "Balance outstanding" : "Still unlinked"}</p><p className="font-semibold tabular-nums" data-testid={`${testPrefix}-ko-balance-${d.invoice_no}`}>{inr(Math.abs(k.balance))}</p></div>
                    </div>
                    <p className="text-[11px] text-slate-500 mb-1">{k.status}</p>
                    <ul className="text-[11px] text-slate-600 space-y-0.5">
                      {k.links.map((x, i) => <li key={i} className="font-mono">{x.doc}{x.ref ? ` (${x.ref})` : ""} · {inr(x.amount)} <span className="font-sans text-slate-400">— {x.basis}</span></li>)}
                    </ul>
                  </>
                ) : <p className="text-xs text-slate-400">No payment linked in SAP or mentioned in a narration – {d.category === "Advance" ? "advance / payment on account, not adjusted to a bill" : "full amount outstanding"}.</p>}
              </div>
            )}
            {!(k && k.links.length) && <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`${testPrefix}-payments-${d.invoice_no}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{isAP ? "Payments / Clearings Made" : "Receipts Recorded"}</p>
              {(!d.payments || d.payments.length === 0)
                ? <p className="text-xs text-slate-400">{isAP && ko ? "No partial clearings posted on this item in SAP." : noneMsg}</p>
                : <PaymentsTable payments={d.payments} />}
            </div>}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}
